import { CodeBlock } from '@lumapps/wrex-code-block/components/blocks/CodeBlock';
import { ImageGroup } from '@lumapps/wrex-enhanced-image/components/blocks/ImageGroup';
import { Link } from '@lumapps/wrex-link/components/blocks/Link';
import { ListItem } from '@lumapps/wrex-list/components/blocks/ListItem';
import { OrderedList } from '@lumapps/wrex-list/components/blocks/OrderedList';
import { UnorderedList } from '@lumapps/wrex-list/components/blocks/UnorderedList';
import { ORDERED_LIST, UNORDERED_LIST } from '@lumapps/wrex-list/constants';
import { ListEditor } from '@lumapps/wrex-list/types';
import { QuoteBlock } from '@lumapps/wrex-quote/components/blocks/QuoteBlock';
import { QuoteEditor } from '@lumapps/wrex-quote/types';
import type { Snippets } from '@lumapps/wrex-snippet/types';
import { Table, TD, TR } from '@lumapps/wrex-table/components/blocks';
import { Divider } from '@lumapps/wrex-typography/components/blocks/Divider';
import { Heading } from '@lumapps/wrex-typography/components/blocks/Heading';
import { InlineCode } from '@lumapps/wrex-typography/components/blocks/InlineCode';
import { Bold, Italic, Strikethrough } from '@lumapps/wrex-typography/components/blocks/Mark';
import { Paragraph } from '@lumapps/wrex-typography/components/blocks/Paragraph';
import { HEADING } from '@lumapps/wrex-typography/constants';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { WrexEditor } from '@lumapps/wrex/types';

/**
 * Exhaustive list of all markdown supported elements
 * @see https://www.markdownguide.org/cheat-sheet/
 */
export const WREX_MARKDOWN_ELEMENTS = [
    Paragraph,
    Heading,
    Link,
    UnorderedList,
    OrderedList,
    ListItem,
    CodeBlock,
    InlineCode,
    Table,
    TD,
    TR,
    QuoteBlock,
    ImageGroup,
    Divider,
];

/**
 * Exhaustive list of all markdown supported marks
 * @see https://www.markdownguide.org/cheat-sheet/
 */
export const WREX_MARKDOWN_MARKS = [Bold, Italic, Strikethrough];

/** Different markdown snippets to insert wrex elements
 *
 * This should be used with `withSnippet` plugin
 */
export const WREX_MARKDOWN_SNIPPETS = {
    /** `#` should insert a heading level 1 */
    '#': (editor: WrexEditor<TypographyEditor>) => editor.changeBlockType(HEADING),
    /** `##` should insert a heading level 2 */
    '##': (editor: WrexEditor<TypographyEditor>) => {
        editor.changeBlockType(HEADING);
        editor.changeHeadingLevel(2);
    },
    /** `###` should insert a heading level 3 */
    '###': (editor: WrexEditor<TypographyEditor>) => {
        editor.changeBlockType(HEADING);
        editor.changeHeadingLevel(3);
    },
    /** `####` should insert a heading level 4 */
    '####': (editor: WrexEditor<TypographyEditor>) => {
        editor.changeBlockType(HEADING);
        editor.changeHeadingLevel(4);
    },
    /** `#####` should insert a heading level 5 */
    '#####': (editor: WrexEditor<TypographyEditor>) => {
        editor.changeBlockType(HEADING);
        editor.changeHeadingLevel(5);
    },
    /** `######` should insert a heading level 6 */
    '######': (editor: WrexEditor<TypographyEditor>) => {
        editor.changeBlockType(HEADING);
        editor.changeHeadingLevel(6);
    },
    /** `>` should insert a quote */
    '>': (editor: WrexEditor<QuoteEditor>) => editor.toggleQuote(),
    /** `---` OR `***` should insert a divider */
    '---|\\*\\*\\*': (editor: WrexEditor<TypographyEditor>) => editor.insertDivider(),
    /** `-` OR `*` should insert an unordered list */
    '-|\\*': (editor: WrexEditor<ListEditor>) => editor.setListType(UNORDERED_LIST),
    /** `{number}.` should insert an ordered list */
    '\\d+\\.': (editor: WrexEditor<ListEditor>) => editor.setListType(ORDERED_LIST),
} as Snippets;
